import { useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTablePagination } from '@speeki/global-ui-components';

import { ColumnDefinitionType } from '@components/molecules/ListTable/ListTable';

import { DisclosureUserListDocument } from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { authVar, redirectToDisclosureVar } from '@boot/reactiveVariables';
import { useOpenInfoModal } from '@domains/disclosures/useOpenInfoModal';

interface SearchForm {
    search: string;
}

interface Column {
    id: string;
    name: string;
}

export const useDisclosuresListPage = () => {
    const [searchValue, setSearchValue] = useState('');
    const isGuest = useReactiveVar(authVar).isGuest;

    const { control, reset } = useForm<SearchForm>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { openInfoModal } = useOpenInfoModal();

    const { field } = useController({
        control,
        defaultValue: '',
        name: 'search',
    });

    const columns: ColumnDefinitionType<Column>[] = [
        {
            flex: 5,
            isMain: true,
            key: 'name',
            label: t('DISCLOSURE_LIST_HEADER_NAME'),
        },
    ];

    const [getDisclosuresList, { data: disclosureListData, loading }] =
        useLazyQuery(DisclosureUserListDocument, {
            fetchPolicy: 'cache-and-network',
        });
    const disclosureList = disclosureListData?.list.edges?.map(
        (edge) => edge.node,
    );

    const pagination = useTablePagination({
        onPageChange: ({ limit, offset, searchPhrase }) => {
            void getDisclosuresList({
                variables: {
                    input: {
                        paging: {
                            limit,
                            offset,
                        },
                        sorting: [
                            {
                                direction: 'DESC',
                                field: 'LAST_UPDATED_AT',
                            },
                        ],
                        textQuery: searchPhrase,
                    },
                },
            });
        },
        searchPhrase: searchValue,
        testId: '',
        totalCount: disclosureListData?.list.totalCount ?? 0,
    });

    const openDisclosureDetails = (disclosureUuid: string) => {
        if (isGuest) {
            redirectToDisclosureVar(true);
            return openInfoModal(disclosureUuid, true);
        }

        const disclosure = disclosureList?.find(
            (disclosure) => disclosure.id === disclosureUuid,
        );

        if (!disclosure) return;
        else if (!disclosure.hasAnswered) return openInfoModal(disclosureUuid);

        navigate(
            getPath({
                params: { disclosureUuid },
                path: '/disclosure/list/:disclosureUuid',
            }),
        );
    };

    const onSearch = () => {
        setSearchValue(field.value);
        pagination.gotoPage(1);
    };

    const onReset = () => {
        setSearchValue('');
        reset();
        pagination.gotoPage(1);
    };

    return {
        columns,
        disclosureList,
        field,
        loading,
        onReset,
        onSearch,
        openDisclosureDetails,
        pagination,
        searchValue,
        t,
    };
};
